import React, { useState, useCallback } from 'react';
import Toggle from 'components/common/toggle';
import * as Styles from '../style';
import { Icon } from 'semantic-ui-react';
import { P } from 'components/reusable-css';
import { useTranslation } from 'react-i18next';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import EditRuleForm from './EditRuleForm';
import AddRuleForm from './AddRuleForm';
import { Formik } from 'formik';
import {
  ALL,
  editNewRuleInitial,
  editNewRuleValidationSchema,
  handleConvert,
  INPUT,
  OBJECT_DROPDOWN,
  PORT_DROPDOWN,
} from '../utils';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import SlidingPanels from 'components/common/sliding-panel';
import CreateObjectPanel from '../objects/CreateObjectPanel';
import axios from 'axios';
import { BASE_URL } from 'constants/env';

const SortableRuleCard = ({ rule, children }) => {
  const itemId = rule.id ? String(rule.id) : `order-${rule.order}`;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: itemId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    zIndex: isDragging ? 999 : undefined,
  };

  return (
    <div ref={setNodeRef} style={{ position: 'relative' }}>
      {children({ dragListeners: listeners, dragAttributes: attributes, sortableStyle: style })}
    </div>
  );
};

const RulesCardList = ({
  firewallServies,
  ruleSetArr,
  setRuleSetArr,
  isLoading,
  protocolsArr,
  objectArr,
  isRefetching,
  isOpenCreateObj,
  setIsOpenCreateObj,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const match = useMediaQuery('(max-width:550px)');
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [indexx, setIndexx] = useState('');
  const [insertIndex, setInsertIndex] = useState(null);
  const [item, setItem] = useState({});

  const uniqueProtocol =
    protocolsArr?.network_protocols?.length > 0
      ? protocolsArr?.network_protocols?.map((data) => {
          return { text: data.protocol, value: data.protocol };
        })
      : [{ text: '', value: '' }];

  const uniqueObjects =
    objectArr?.firewall_object?.length > 0
      ? objectArr?.firewall_object?.map((data) => {
          return { text: handleConvert(data.label), value: data.id, type: data.type };
        })
      : [{ text: '', value: '', type: '' }];

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const getItemId = useCallback((rule) => (rule.id ? String(rule.id) : `order-${rule.order}`), []);

  const findIndexById = useCallback(
    (id) => ruleSetArr.findIndex((r) => getItemId(r) === String(id)),
    [ruleSetArr, getItemId],
  );

  const handleDragStart = (event) => {
    const { active } = event;
    const startIndex = findIndexById(active.id);
    setInsertIndex(startIndex);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) {
      setInsertIndex(null);
      return;
    }
    const activeIndex = findIndexById(active.id);
    const overIndex = findIndexById(over.id);

    if (activeIndex === -1 || overIndex === -1) {
      setInsertIndex(null);
      return;
    }

    if (overIndex > activeIndex) {
      setInsertIndex(overIndex + 1);
    } else {
      setInsertIndex(overIndex);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setInsertIndex(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = findIndexById(active.id);
    const newIndex = findIndexById(over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newRules = arrayMove(ruleSetArr, oldIndex, newIndex).map((r, idx) => ({
      ...r,
      order: idx + 1,
    }));

    setRuleSetArr(newRules);
  };

  const handleDragCancel = () => {
    setInsertIndex(null);
  };

  const handleDelete = (index) => {
    const newCards = ruleSetArr.map((card, i) => {
      if (i === index) {
        return {
          ...card,
          isDeleted: 1, // mark as deleted
        };
      }
      return card;
    });
    setRuleSetArr(newCards);
  };

  const handleEdit = (rule) => {
    setEditingRuleId(rule.order);
    setIndexx('');
  };

  const handleClickAdd = (index, rule) => {
    setInsertIndex(index);
    setIndexx(rule.order);
    setEditingRuleId(null);
  };

  const handleEnabledToggle = (values, enabledValue, ruleOrder) => {
    const newObject = {
      action: values.action,
      protocol: values.protocol,
      //src
      src: values.src_checkbox === ALL ? ALL : values.src_checkbox === INPUT ? values.src_ip_input : '',
      src_object: values.src_checkbox === OBJECT_DROPDOWN ? values.src_object_dropdown : '',
      //dst ip
      dst_ip: values.dst_ip_checkbox === ALL ? ALL : values.dst_ip_checkbox === INPUT ? values.dst_ip_input : '',
      dst_ip_object: values.dst_ip_checkbox === OBJECT_DROPDOWN ? values.dst_ip_object_dropdown : '',
      // dst service
      dst_service:
        values.dst_service_checkbox === ALL
          ? ALL
          : values.dst_service_checkbox === PORT_DROPDOWN
          ? values.dst_service_dropdown
          : values.dst_service_checkbox === INPUT
          ? values.dst_service_input
          : '',
      dst_service_object: values.dst_service_checkbox === OBJECT_DROPDOWN ? values.dst_service_object_dropdown : '',
    };
    setRuleSetArr((prev) =>
      prev.map((rule) =>
        rule.order == ruleOrder
          ? { ...newObject, enabled: enabledValue, order: rule.order } // Apply changes, importantly, KEEP the original rule.order
          : rule,
      ),
    );
  };

  const handleClose = () => {
    setEditingRuleId(null);
  };

  const handleEditRule = (values, resetForm) => {
    const newObject = {
      enabled: values.enabled,
      action: values.action,
      protocol: values.protocol,
      //src
      src: values.src_checkbox === ALL ? ALL : values.src_checkbox === INPUT ? values.src_ip_input : '',
      src_object: values.src_checkbox === OBJECT_DROPDOWN ? values.src_object_dropdown : '',
      //dst ip
      dst_ip: values.dst_ip_checkbox === ALL ? ALL : values.dst_ip_checkbox === INPUT ? values.dst_ip_input : '',
      dst_ip_object: values.dst_ip_checkbox === OBJECT_DROPDOWN ? values.dst_ip_object_dropdown : '',
      // dst service
      dst_service:
        values.dst_service_checkbox === ALL
          ? ALL
          : values.dst_service_checkbox === PORT_DROPDOWN
          ? values.dst_service_dropdown
          : values.dst_service_checkbox === INPUT
          ? values.dst_service_input
          : '',
      dst_service_object: values.dst_service_checkbox === OBJECT_DROPDOWN ? values.dst_service_object_dropdown : '',
    };

    setRuleSetArr((prev) =>
      prev.map((rule) =>
        rule.order == editingRuleId
          ? { ...newObject, order: rule.order } // Apply changes, importantly, KEEP the original rule.order
          : rule,
      ),
    );

    handleClose();
    resetForm();
  };

  const handleCreateObjModal = () => {
    setIsOpenCreateObj(!isOpenCreateObj);
  };

  const filterObjectById = (id) => uniqueObjects.find((obj) => obj.value === id)?.text || '';

  const itemsIds = ruleSetArr.map((r) => getItemId(r));

  const handleOpenObjectDetail = async (objId) => {
    if (objId) {
      setItem({});
      setIsOpenCreateObj(true);
      try {
        const promise = await axios.get(`${BASE_URL}/vs/firewall/object/${objId}`);
        let objects = objectArr?.firewall_object?.find((obj) => obj.id === objId);
        setItem({ list: promise.data, ...objects });
      } catch (error) {
        setIsOpenCreateObj(false);
      }
    }
  };

  return (
    <Styles.ListContainers style={{ pointerEvents: isRefetching ? 'none' : 'auto', opacity: isRefetching ? 0.6 : 1 }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={itemsIds}>
          {isDesktop ? (
            <Box>
              {ruleSetArr?.length > 0 && (
                <Styles.Headers>
                  <Styles.Column>
                    <P>{t('vs/firewall.string20')}</P>
                  </Styles.Column>
                  <Styles.Column>
                    <P>{t('vs/firewall.string21')}</P>
                  </Styles.Column>
                  <Styles.Column>
                    <P>{t('vs/firewall.string22')}</P>
                  </Styles.Column>
                  <Styles.Column>
                    <P>{t('vs/firewall.string67')}</P>
                  </Styles.Column>
                  <Styles.Column>
                    <P>{t('vs/firewall.string24')}</P>
                  </Styles.Column>
                  <Styles.Column>
                    <P>{t('vs/firewall.string25')}</P>
                  </Styles.Column>
                  <Styles.Column>
                    <P>{t('vs/firewall.string23')}</P>
                  </Styles.Column>
                  <Styles.Column />
                </Styles.Headers>
              )}
              {ruleSetArr.map((rule, index) => (
                <SortableRuleCard key={getItemId(rule)} rule={rule}>
                  {({ dragListeners, dragAttributes, sortableStyle }) => (
                    <>
                      <Formik
                        initialValues={editNewRuleInitial(rule, firewallServies, uniqueObjects)}
                        validationSchema={editNewRuleValidationSchema}
                        enableReinitialize
                        onSubmit={(values, { resetForm }) => handleEditRule(values, resetForm)}
                      >
                        {(formik) =>
                          editingRuleId == rule.order ? (
                            <EditRuleForm
                              handleClose={handleClose}
                              formik={formik}
                              firewallServies={firewallServies}
                              uniqueProtocol={uniqueProtocol}
                              objectArr={uniqueObjects}
                            />
                          ) : (
                            <Styles.Card style={sortableStyle} isDeleted={rule?.isDeleted}>
                              <Styles.Column>{rule.order}</Styles.Column>
                              <Styles.Column style={{ marginBottom: '-5px' }}>
                                <Toggle
                                  value="active"
                                  checked={rule.enabled == 1}
                                  handleChange={(e) => {
                                    const value = e.target.checked ? 1 : 0;
                                    formik.setFieldValue('enabled', value);
                                    handleEnabledToggle(formik.values, value, rule.order);
                                  }}
                                />
                              </Styles.Column>
                              <Styles.Column>
                                <Styles.ActionBadge type={rule.action}>
                                  {t(rule.action == 'allow' ? 'vs/firewall.string54' : 'vs/firewall.string55')}
                                </Styles.ActionBadge>
                              </Styles.Column>
                              <Styles.SingleColumn>
                                <P>{rule.protocol == ALL ? t('vs/firewall.string83') : rule.protocol}</P>
                              </Styles.SingleColumn>
                              <Styles.SingleColumn onClick={() => handleOpenObjectDetail(rule.src_object)}>
                                <P className={`${rule.src_object ? 'text-link' : ''}`}>
                                  {rule.src == ALL || rule.src == '0/0'
                                    ? t('vs/firewall.string77')
                                    : rule.src || filterObjectById(rule.src_object)}
                                </P>
                              </Styles.SingleColumn>
                              <Styles.SingleColumn onClick={() => handleOpenObjectDetail(rule.dst_ip_object)}>
                                <P className={`${rule.dst_ip_object ? 'text-link' : ''}`}>
                                  {rule.dst_ip == ALL || rule.dst_ip == '0/0'
                                    ? t('vs/firewall.string76')
                                    : rule.dst_ip || filterObjectById(rule.dst_ip_object)}
                                </P>
                              </Styles.SingleColumn>
                              <Styles.SingleColumn onClick={() => handleOpenObjectDetail(rule.dst_service_object)}>
                                <P className={`${rule.dst_service_object ? 'text-link' : ''}`}>
                                  {rule.dst_service || filterObjectById(rule.dst_service_object)}
                                </P>
                              </Styles.SingleColumn>
                              {rule?.isDeleted ? (
                                <Styles.Column style={{ justifyContent: 'flex-end' }}>
                                  <P className="orange-text">{t('vs/firewall.string84')}</P>
                                </Styles.Column>
                              ) : (
                                <Styles.Column style={{ justifyContent: 'flex-end' }}>
                                  <Styles.Icon onClick={() => handleEdit(rule)}>
                                    <Icon name="pencil" size="large" />
                                  </Styles.Icon>
                                  <Styles.Icon onClick={() => handleDelete(index)}>
                                    <Icon name="trash alternate outline" size="large" />
                                  </Styles.Icon>
                                  <Styles.ThreeDotsIcon {...dragListeners} {...dragAttributes}>
                                    <DragIndicatorIcon fontSize="large" style={{ color: '#FF7300' }} />
                                  </Styles.ThreeDotsIcon>
                                </Styles.Column>
                              )}
                            </Styles.Card>
                          )
                        }
                      </Formik>
                      {indexx != rule.order && (
                        <Styles.StyledPlusIcon onClick={() => handleClickAdd(index, rule)}>
                          <div>+</div>
                        </Styles.StyledPlusIcon>
                      )}
                      {indexx == rule.order && (
                        <AddRuleForm
                          setRuleSetArr={setRuleSetArr}
                          setIndexx={setIndexx}
                          rules={ruleSetArr}
                          firewallServies={firewallServies}
                          insertIndex={insertIndex}
                          uniqueProtocol={uniqueProtocol}
                          objectArr={uniqueObjects}
                        />
                      )}
                    </>
                  )}
                </SortableRuleCard>
              ))}
            </Box>
          ) : (
            <Box>
              {ruleSetArr.map((rule, index) => (
                <SortableRuleCard key={getItemId(rule)} rule={rule}>
                  {({ dragListeners, dragAttributes, sortableStyle }) => (
                    <>
                      <Formik
                        initialValues={editNewRuleInitial(rule, firewallServies, uniqueObjects)}
                        validationSchema={editNewRuleValidationSchema}
                        enableReinitialize
                        onSubmit={(values, { resetForm }) => handleEditRule(values, resetForm)}
                      >
                        {(formik) => {
                          return editingRuleId == rule.order ? (
                            <EditRuleForm
                              handleClose={handleClose}
                              formik={formik}
                              firewallServies={firewallServies}
                              uniqueProtocol={uniqueProtocol}
                              objectArr={uniqueObjects}
                            />
                          ) : (
                            <Styles.MobileCard isDeleted={rule?.isDeleted} style={sortableStyle}>
                              <Styles.FlexBetweenColumn>
                                <div>
                                  <P className="m-0">{t('vs/firewall.string20')}:</P>
                                  <P className="m-0">{rule.order}</P>
                                </div>

                                {rule?.isDeleted ? (
                                  <Styles.Column style={{ justifyContent: 'flex-end' }}>
                                    <P className="orange-text">{t('vs/firewall.string84')}</P>
                                  </Styles.Column>
                                ) : (
                                  <Styles.Column>
                                    <Styles.Icon onClick={() => handleEdit(rule)}>
                                      <Icon name="pencil" size="large" />
                                    </Styles.Icon>
                                    <Styles.Icon onClick={() => handleDelete(index)}>
                                      <Icon name="trash alternate outline" size="large" />
                                    </Styles.Icon>
                                    <Styles.ThreeDotsIcon {...dragListeners} {...dragAttributes}>
                                      <DragIndicatorIcon fontSize="large" style={{ color: '#FF7300' }} />
                                    </Styles.ThreeDotsIcon>
                                  </Styles.Column>
                                )}
                              </Styles.FlexBetweenColumn>
                              <Styles.FlexBetweenColumnBorder>
                                <div>
                                  <P className="m-0">{t('vs/firewall.string21')}:</P>
                                  <Toggle
                                    value={'active'}
                                    checked={rule.enabled == 1}
                                    handleChange={(e) => {
                                      let value = e.target.checked ? 1 : 0;
                                      formik.setFieldValue('enabled', value);
                                      handleEnabledToggle(formik.values, value, rule.order);
                                    }}
                                  />
                                </div>
                                <div>
                                  <P className="m-0">{t('vs/firewall.string22')}:</P>
                                  <Styles.MobileActionBadge type={rule.action}>
                                    {t(rule.action == 'allow' ? 'vs/firewall.string54' : 'vs/firewall.string55')}
                                  </Styles.MobileActionBadge>
                                </div>
                                <div>
                                  <P className="m-0">{t('vs/firewall.string67')}:</P>
                                  <P className="m-0">{rule.protocol}</P>
                                </div>
                                <Styles.SingleColumnMobile>
                                  <P>{t('vs/firewall.string23')}:</P>
                                  <P
                                    className={`${rule.dst_service_object ? 'text-link' : ''}`}
                                    onClick={() => handleOpenObjectDetail(rule.dst_service_object)}
                                  >
                                    {rule.dst_service || filterObjectById(rule.dst_service_object)}
                                  </P>
                                </Styles.SingleColumnMobile>
                              </Styles.FlexBetweenColumnBorder>
                              <Styles.FlexBetweenColumn>
                                <Styles.SingleColumnMobile>
                                  <P>{t('vs/firewall.string24')}:</P>
                                  <P
                                    className={`${rule.src_object ? 'text-link' : ''}`}
                                    onClick={() => handleOpenObjectDetail(rule.src_object)}
                                  >
                                    {rule.src == ALL || rule.src == '0/0'
                                      ? t('vs/firewall.string77')
                                      : rule.src || filterObjectById(rule.src_object)}
                                  </P>
                                </Styles.SingleColumnMobile>
                                <Styles.SingleColumnMobile>
                                  <P>{t('vs/firewall.string25')}:</P>
                                  <P
                                    className={`${rule.dst_ip_object ? 'text-link' : ''}`}
                                    onClick={() => handleOpenObjectDetail(rule.dst_ip_object)}
                                  >
                                    {rule.dst_ip == ALL || rule.dst_ip == '0/0'
                                      ? t('vs/firewall.string76')
                                      : rule.dst_ip || filterObjectById(rule.dst_ip_object)}
                                  </P>
                                </Styles.SingleColumnMobile>
                              </Styles.FlexBetweenColumn>
                            </Styles.MobileCard>
                          );
                        }}
                      </Formik>
                      {indexx != rule.order && (
                        <Styles.StyledPlusIcon onClick={() => handleClickAdd(index, rule)}>
                          <div>+</div>
                        </Styles.StyledPlusIcon>
                      )}
                      {indexx == rule.order && (
                        <AddRuleForm
                          setRuleSetArr={setRuleSetArr}
                          setIndexx={setIndexx}
                          rules={ruleSetArr}
                          firewallServies={firewallServies}
                          insertIndex={insertIndex}
                          uniqueProtocol={uniqueProtocol}
                          objectArr={uniqueObjects}
                        />
                      )}
                    </>
                  )}
                </SortableRuleCard>
              ))}
            </Box>
          )}
        </SortableContext>
      </DndContext>
      {ruleSetArr?.length <= 0 && isLoading == false && ruleSetArr != undefined && (
        <AddRuleForm
          setIndexx={setIndexx}
          setRuleSetArr={setRuleSetArr}
          rules={ruleSetArr}
          firewallServies={firewallServies}
          insertIndex={insertIndex}
          uniqueProtocol={uniqueProtocol}
          objectArr={uniqueObjects}
        />
      )}
      {isOpenCreateObj && (
        <SlidingPanels size={match ? 93 : 50} isOpen={isOpenCreateObj} isClose={setIsOpenCreateObj}>
          <CreateObjectPanel item={item} label={handleConvert(item?.label)} handleCloseModal={handleCreateObjModal} />
        </SlidingPanels>
      )}
    </Styles.ListContainers>
  );
};

export default RulesCardList;
